import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, switchMap } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { CartConstants, CartItemImg } from '@api/cart/cart.models';
import { Product, lowThreshold } from '@api/catalog/products/products.models';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { StaticResourceService } from '@core/services/static-resource.service';
import { UiService } from '@core/services/ui.service';
import { AnalyticsEvent } from '@shared/analytics/store/analytics.actions';
import { GoToSection } from '@shared/navigation/store/navigation.actions';
import { Discount, PriceStyles, PricesClasses, ProductMapPrices } from '@shared/prices/models/product-prices.model';
import { PricesModule } from '@shared/prices/prices.module';
import { ProductPriceService } from '@shared/prices/services/product-prices.service';
import {
    ProductCartService,
} from '@shared/product/modals/product-added-to-cart-modal/product-cart/product-cart.service';
import { getShopCartItemsStocks } from '@shared/shopcart/store/shopcart.selectors';

@Component({
    selector: 'product-cart',
    templateUrl: './product-cart.component.html',
    styleUrls: ['./product-cart.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        TranslateModule,
        PricesModule,
    ],
    providers: [
        ProductCartService,
        ProductPriceService,
    ],
})
export class ProductCartComponent implements OnInit {
    @Input() public product: Product;

    @Input() public hidePriceText: boolean;

    @Output() public readonly clickProduct: EventEmitter<Product> = new EventEmitter();

    public hasStockAllSizes: boolean;

    public isGift: boolean = false;

    public showColor: boolean = true;

    public showSize: boolean = true;

    public oneSize: boolean = false;

    public sizeName: string = '';

    public colorName: string = '';

    public inactiveClass: string = '';

    public itemImagesUrl: CartItemImg;

    public itemPrices$: Observable<ProductMapPrices>;

    public classPrices$: Observable<PricesClasses>;

    public discountText$: Observable<Discount>;

    public giftImage: string = this.staticResourceService.getImage('/giftTicket/gift-box.jpg', true);

    public priceStyles$: Observable<PriceStyles>;

    public isLastUnits$: Observable<boolean>;

    constructor(
        private readonly store: Store,
        private readonly uiService: UiService,
        private readonly productPriceService: ProductPriceService,
        private readonly productCartService: ProductCartService,
        private readonly staticResourceService: StaticResourceService,
        @Inject(WINDOW) private readonly window: CommonWindow
    ) {
    }

    public ngOnInit(): void {
        this.setOptionsDefault();
    }

    public openSuscriptionJoin(showSuscriptionCTA: boolean) {
        if (showSuscriptionCTA) {
            this.store.dispatch(AnalyticsEvent({
                eventData: {
                    cf: 'catalogo',
                    category: 'productos_relacionados',
                    action: 'unete_feel',
                },
            }));
            this.store.dispatch(GoToSection({ url: 'join-VIP' }));
        } else {
            // TODO ECOMDUTI-21031 Add conditions when internationalization is available (phase 2)
        }
    }

    public clickOnProduct() {
        this.clickProduct.emit(this.product);
    }

    private setOptionsDefault(): void {
        this.hasStockAllSizes = (this.product.visibilityValue?.toString() !== CartConstants.SHOW ? 0 : 1) > 0;

        if (!this.hasStockAllSizes) {
            this.inactiveClass = 'type-inactive';
        }

        this.isGift = this.product.reference.toUpperCase().includes(CartConstants.XGIFT);

        const { oneSize, sizeName } = this.productCartService.setSizeValues(this.product);
        this.oneSize = oneSize;
        this.sizeName = sizeName;

        if (this.product.status.selectedColor.name) {
            this.colorName = this.product.status.selectedColor.name.charAt(0).toUpperCase()
                + this.product.status.selectedColor.name.slice(1).toLowerCase();
        }

        if (this.isGift) {
            this.showColor = false;
            this.showSize = false;
        }

        if (this.product.status.hideSize) {
            this.showSize = false;
        }

        this.setImage();

        this.setPricesInfo();

        this.isLastUnits$ = this.store.select(getShopCartItemsStocks).pipe(
            filter(stocks => !!stocks),
            map(stocks => stocks[this.product?.status?.selectedSize?.sku]?.typeThreshold === lowThreshold),
        );
    }

    private setImage() {
        const widthImage = this.window.innerWidth / (this.uiService.ui.device.mobile ? 2 : 5);
        this.itemImagesUrl = this.productCartService.getImageItem(
            this.product,
            widthImage
        );
    }

    private setPricesInfo() {
        const hasPromotion = !!this.product?.status?.selectedSize?.promotionId;
        this.itemPrices$ = this.productPriceService.getPrices(this.product);
        this.classPrices$ = this.itemPrices$.pipe(
            switchMap((prices) => this.productPriceService
                .getClassPrices(prices, hasPromotion, true)),
            map((classPrices) => ({
                ...classPrices,
                primaryPrice: classPrices.primaryPrice.replace('text-m', 'title-m'),
                secondaryPrice: classPrices.secondaryPrice.replace('text-m', 'title-m'),
                terciaryPrice: classPrices.terciaryPrice.replace('text-m', 'title-m'),
                container: 'text-nowrap text-m',
                alternativeCurrencies: 'title-l-b',
            }))
        );
        this.discountText$ = this.itemPrices$.pipe(
            switchMap((prices) => this.productPriceService.getDiscountText(prices))
        );

        this.priceStyles$ = this.itemPrices$.pipe(
            switchMap((prices) => this.productPriceService
                .getPriceStyles(prices, hasPromotion))
        );
    }
}
